import { LineItemData as LineItemModel, Price } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import {
  CartMobileLineItem,
  Image,
  LineItemActions,
  LineItemNote,
  LoadingSkeleton,
  QuantitySelector,
  SmartLink,
} from '@components';
import { useCart } from '@hooks/cart/cart.hooks';
import { Box, Stack, styled } from '@mui/material';
import { breakpoints } from '@themes/theme.helpers';
import { t } from '@translations';
import { getImageResizerUrl } from '@utils/images.utils';
import { formatPrice } from '@utils/price.utils';
import { insertWordBreaks } from '@utils/text.utils';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';

// STYTLES
//---------
export const CartItemWrapper = styled(Stack)`
  padding: 16px 0 8px;
  background: #fff;
  display: flex;
  width: 100%;
  flex-direction: row;
  gap: 8px;
`;

export const CartBox = styled(Box)`
  background-color: #fff;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const CartCell = styled(Box)`
  display: flex;

  width: 120px;
  &.long {
    width: 150px;
  }
  &.quantity {
    width: 102px;
    ${breakpoints.down('md')} {
      width: 74px;
    }
  }
  &.image-column {
    width: 116px;
  }
  align-items: center;
  justify-content: center;

  &.bold {
    font-weight: 700;
  }
  &.uppercase {
    text-transform: uppercase;
  }
  &.long {
    justify-content: flex-start;
  }

  &.actions {
    width: 80px;
    text-align: right;
  }

  &.actions-with-favorites {
    width: 60px;
    text-align: right;
    ${breakpoints.down('lx')} {
      width: 80px;
    }
    ${breakpoints.down('lsm')} {
      width: 48px;
    }
  }

  &.center {
    justify-content: center;
    text-align: right;
  }
`;

const ImageWrapper = styled(Box)`
  img {
    height: 100px;
    width: 100px;
  }
`;

const NameWrapper = styled(Box)`
  font-weight: 700;
  text-transform: uppercase;
  word-break: break-word;
`;

const SerialNumbers = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

/**
 * Order Item
 */
export type LineItemProps = {
  lineItem?: LineItemModel;
  isEditable?: boolean;
};

/**
 * Single Order item
 */
export const CartLineItem = (props: LineItemProps) => {
  const { lineItem = {}, isEditable } = props;
  const {
    name,
    code,
    productNo = '',
    price,
    placedPrice,
    serialNumbers,
    imageUrl = '',
    quantity = 0,
    url = '',
    regularPrice,
    discount,
  } = lineItem;

  const resizedImageUrl = getImageResizerUrl(imageUrl, { width: 100, format: 'webp' });

  // Change amount in cart
  const [localQuantity, setLocalQuantity] = useState<number>(quantity);

  useEffect(() => {
    setLocalQuantity(quantity);
  }, [quantity, setLocalQuantity]);

  // BE request to change cart
  const { changeItemQuantity } = useCart();
  const debouncedUpdateQuantity = useMemo(
    () =>
      debounce((qty: number) => {
        changeItemQuantity({ code, quantity: qty, productName: name });
      }, 1000),
    [code, name, changeItemQuantity]
  );

  // Update BE cart when quantity changes
  useEffect(() => {
    if (quantity !== localQuantity) {
      debouncedUpdateQuantity(localQuantity);
    }
    return () => {
      debouncedUpdateQuantity.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localQuantity]);

  // Calculate total
  const netPrice = price ?? placedPrice ?? ({} as Price);
  const totalNetPrice = { ...netPrice, amount: netPrice.amount * quantity } as Price;

  return (
    <>
      <CartMobileLineItem
        {...props}
        quantity={localQuantity}
        setQuantity={setLocalQuantity}
        netPrice={netPrice}
        totalNetPrice={totalNetPrice}
        sx={{ display: { xs: 'block', sm: 'none' }, height: '100%' }}
      />
      <CartBox
        sx={{
          display: {
            xs: 'none',
            sm: 'flex',
          },
          height: '100%',
        }}
      >
        <CartItemWrapper sx={{ borderRadius: 1 }}>
          <CartCell sx={{ width: resizedImageUrl ? 'auto' : '100px', ml: 2 }}>
            <ImageWrapper>
              <Image image={resizedImageUrl} width='100' height='100' />
            </ImageWrapper>
          </CartCell>
          <CartCell className='long' sx={{ height: '100%' }}>
            <Stack direction='column' gap={1} sx={{ height: '100%', justifyContent: 'center' }}>
              <SmartLink href={url}>
                <NameWrapper
                  dangerouslySetInnerHTML={{ __html: insertWordBreaks(name) }}
                ></NameWrapper>
              </SmartLink>
              <Box sx={{ width: 'min-content' }}>
                {t(TextCodes.Product.Details.ProductNumber, { productNo })}
              </Box>
            </Stack>
          </CartCell>
          {serialNumbers && (
            <CartCell>
              <SerialNumbers>
                {serialNumbers?.map((x, key) => (
                  <Box key={key}>{x}</Box>
                ))}
              </SerialNumbers>
            </CartCell>
          )}
          <CartCell
            sx={{
              display: { xs: 'none', md: 'flex', lmd: 'none', lg: 'flex' },
            }}
          >
            {formatPrice(regularPrice)}
          </CartCell>

          <CartCell
            sx={{
              display: { xs: 'none', lx: 'flex' },
            }}
          >
            {formatPrice(discount)}
          </CartCell>

          <CartCell>
            <Box
              component='span'
              sx={{
                display: {
                  xs: 'none',
                  sm: 'block',
                  lsm: 'none',
                },
              }}
            >
              {localQuantity} x &nbsp;
            </Box>
            {formatPrice(netPrice)}
          </CartCell>

          <CartCell
            className='quantity bold center'
            sx={{
              display: { xs: 'none', lsm: 'flex' },
            }}
          >
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
              }}
            >
              <QuantitySelector
                dark
                quantity={localQuantity}
                setQuantity={setLocalQuantity}
                size='small'
              />
            </Box>
            <Box
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {localQuantity}
            </Box>
          </CartCell>

          <CartCell className='bold'>{formatPrice(totalNetPrice)}</CartCell>

          <CartCell
            className='actions-with-favorites'
            sx={{
              '& .line-item-wishlist': {
                display: {
                  xs: 'none',
                  lsm: 'block',
                  md: 'none',
                  lx: 'block',
                },
              },
            }}
          >
            <LineItemActions lineItem={lineItem} />
          </CartCell>
        </CartItemWrapper>
        <LineItemNote lineItem={lineItem} isEditable={isEditable} p={'0 16px 16px'} />
      </CartBox>
    </>
  );
};

export const LineItemPlaceholder = () => (
  <CartItemWrapper sx={{ mb: 2 }}>
    <CartCell className='center' padding={2}>
      <LoadingSkeleton sx={{ width: '100%', height: '100%' }} />
    </CartCell>
    <CartCell className='long center' padding={2}>
      <LoadingSkeleton sx={{ width: '100%', height: '100%' }} />
    </CartCell>
    <CartCell className='center' padding={2}>
      <LoadingSkeleton sx={{ width: '100%', height: '100%' }} />
    </CartCell>
    <CartCell className='center' padding={2}>
      <LoadingSkeleton sx={{ width: '100%', height: '100%' }} />
    </CartCell>
  </CartItemWrapper>
);
